/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import { IFieldDefinitionPanelTextData, IFieldDefinitionPanelTextState } from ".";
import { ILogger, getLogger } from "@nuance/hux-diagnostics";
import { BaseCommands, FormErrors, IFieldChangeData } from "../../State";
import { CmdBuilder } from "@nuance/hap-components";

const logger: ILogger = getLogger();

export class FieldDefinitionPanelTextCommands extends BaseCommands<
    IFieldDefinitionPanelTextData,
    IFieldDefinitionPanelTextState
> {
    constructor() {
        super();
        const cb = new CmdBuilder();
        const cmds = {
            onBlur: cb.bindCmd<IFieldDefinitionPanelTextState, string>(this.OnBlurHandler),
            onTextChanged: cb.bindCmd<IFieldDefinitionPanelTextState, IFieldChangeData>(
                this.OnTextChangedHandler
            ),
            onAccessLevelChanged: cb.bindCmd(this.OnAccessLevelChangedHandler),
            onHelpTextChange: cb.bindCmd(this.OnHelpTextChangeHandler),
            onAuditChanged: cb.bindCmd(this.OnAuditChangedHandler),

            onSpinMaxDecrement: cb.bindCmdNP(this.onSpinMaxDecrementHandler),
            onSpinMaxIncrement: cb.bindCmdNP(this.onSpinMaxIncrementHandler),
            onSpinMinDecrement: cb.bindCmdNP(this.onSpinMinDecrementHandler),
            onSpinMinIncrement: cb.bindCmdNP(this.onSpinMinIncrementHandler),
            onMaxLengthChanged: cb.bindCmd(this.onMaxLengthChangedHandler),
            onMinLengthChanged: cb.bindCmd(this.onMinLengthChangedHandler),
            onIsRequiredChanged: cb.bindCmdNP(this.onIsRequiredChangedHandler),
            onIncludeRegexChanged: cb.bindCmdNP(this.onIncludeRegexChangedHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer<IFieldDefinitionPanelTextState>(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    /**
     * Creates object containing text data to save
     * @param state Old state
     * @returns State data to save
     */
    Save = (state: IFieldDefinitionPanelTextState): IFieldDefinitionPanelTextData => {
        logger.logInformation("Saving text properties");

        // important to make a copy!
        const saveData: IFieldDefinitionPanelTextData = {
            ...state.data
        };

        return saveData;
    };

    /**
     * Validates the form fields.
     *
     * @param state Current state (immutable)
     * @returns Form errors
     */
    Validate(state: IFieldDefinitionPanelTextState): FormErrors<IFieldDefinitionPanelTextData> {
        const errors: FormErrors<IFieldDefinitionPanelTextData> = {};

        // settingName validation
        if (state.data.settingName === "") {
            errors.settingName = "Setting name is required.";
        } else {
            if (state.common.validateSettingName === undefined) {
                logger.logError("Setting name validation function not hooked up");
                throw new Error("Unable to validate setting name");
            }

            errors.settingName = state.common.validateSettingName(
                state.data.settingName,
                state.common.originalSettingName
            );
        }

        // label
        if (state.data.title === "") {
            errors.title = "Label is required.";
        }

        // help text
        if (!state.data.helpTextValid) {
            errors.helpTextValid = "Help text is not valid";
        }

        if (state.data.includeRegex) {
            if (state.data.regexValue === "") {
                errors.regexValue = "Regular expression is required.";
            } else {
                try {
                    new RegExp(state.data.regexValue).test("");
                    if (
                        state.data.defaultValue !== "" &&
                        !new RegExp(state.data.regexValue).test(state.data.defaultValue)
                    ) {
                        errors.defaultValue =
                            "Default value doesn't match with regular expression pattern.";
                    }
                } catch {
                    errors.regexValue = "Invalid regex expression pattern.";
                }
            }
            if (state.data.regexErrorMessage === "") {
                errors.regexErrorMessage = "Error for regular expression is required.";
            }
        }

        return errors;
    }

    /////////////////////////////////////////////////////////////////
    // Command handlers
    /////////////////////////////////////////////////////////////////

    onMaxLengthChangedHandler = (state: IFieldDefinitionPanelTextState, value?: string) => {
        if (value === undefined || isNaN(+value)) {
            logger.logError("Invalid number entered");
        } else {
            state.data.maxLength = +value;
            state.isDirty = true;
        }
        return state;
    };

    onMinLengthChangedHandler = (state: IFieldDefinitionPanelTextState, value?: string) => {
        if (value === undefined || isNaN(+value)) {
            logger.logError("Invalid number entered");
        } else {
            state.data.minLength = +value;
            state.isDirty = true;
        }

        return state;
    };

    onSpinMaxDecrementHandler = (state: IFieldDefinitionPanelTextState) => {
        if (state.data.maxLength - 1 < state.data.minLength) {
            state.data.maxLength = state.data.minLength;
        } else {
            state.data.maxLength -= 1;
        }
        state.isDirty = true;
        return state;
    };

    onSpinMaxIncrementHandler = (state: IFieldDefinitionPanelTextState) => {
        state.data.maxLength += 1;
        state.isDirty = true;
        return state;
    };

    onSpinMinDecrementHandler = (state: IFieldDefinitionPanelTextState) => {
        if (state.data.minLength > 0) {
            state.data.minLength -= 1;
            state.isDirty = true;
        }
        return state;
    };

    onSpinMinIncrementHandler = (state: IFieldDefinitionPanelTextState) => {
        if (state.data.minLength + 1 > state.data.maxLength) {
            state.data.minLength = state.data.maxLength;
        } else {
            state.data.minLength += 1;
            state.isDirty = true;
        }
        return state;
    };

    onIsRequiredChangedHandler = (state: IFieldDefinitionPanelTextState) => {
        state.data.isRequired = !state.data.isRequired;
        state.isDirty = true;
        return state;
    };

    onIncludeRegexChangedHandler = (state: IFieldDefinitionPanelTextState) => {
        state.data.includeRegex = !state.data.includeRegex;
        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);
        state.isDirty = true;
        return state;
    };
}
